import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { PaletteMode } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import debounce from "@mui/material/utils/debounce";
import {
  LG_WIDTH,
  MD_WIDTH,
  SM_WIDTH,
  XL_WIDTH,
  SMD_WIDTH,
  TABLE_PRODUCTS_MIN_WIDTH,
  TABLE_CATEGORIES_MIN_WIDTH,
} from "../constants";

const COLOR_MODE_KEY = "colorMode";
const DISABLE_ANIMATION_KEY = "disableAnimation";

function readColorMode(): PaletteMode {
  if (typeof sessionStorage === "undefined") return "light";
  const v = sessionStorage.getItem(COLOR_MODE_KEY) as PaletteMode | null;
  return v === "dark" || v === "light" ? v : "light";
}

function readAnimationsDisabled(): boolean {
  if (typeof sessionStorage === "undefined") return false;
  return sessionStorage.getItem(DISABLE_ANIMATION_KEY) === "true";
}

export type Dimensions = {
  height: number;
  width: number;
};

export type UIContextValue = {
  isMobile: boolean;
  isTablet: boolean;
  isSmallDevice: boolean;
  isSmallMediumDevice: boolean;
  isMediumDevice: boolean;
  isLargeDevice: boolean;
  isXLargeDevice: boolean;
  isSidekickShowProductsChat: boolean;
  isSidekickShowCategoriesChat: boolean;
  iOS: boolean;
  openSideNav: boolean;
  showSideNav: boolean;
  toggleDrawer: (open: boolean) => void;
  isTopNavHidden: boolean;
  toggleHideTopNav: (isHidden: boolean) => void;
  NAV_CLOSED_DRAWER_WIDTH: number;
  NAV_DRAWER_WIDTH: number;
  colorMode: PaletteMode;
  setColorMode: (m: PaletteMode) => void;
  setColorModeFromBoolean: (isDarkTheme: boolean) => void;
  toggleColorMode: () => void;
  animationsDisabled: boolean;
  setAnimationsDisabledFromBoolean: (v: boolean) => void;
  dimensions: Dimensions;
  isSmallHeightDevice: boolean;
};

export const UIContext = createContext<UIContextValue | null>(null);

export function UIContextProvider({ children }: { children: ReactNode }) {
  const iOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);

  const isSmallDevice = useMediaQuery("(max-width:399px)");
  const isMobile = useMediaQuery(`(max-width:${SM_WIDTH - 1}px)`);
  const isTablet = useMediaQuery(`(min-width:${SM_WIDTH}px) and (max-width:${MD_WIDTH - 1}px)`);
  const isSmallMediumDevice = useMediaQuery(
    `(min-width:${SM_WIDTH}px) and (max-width:${SMD_WIDTH - 1}px)`,
  );
  const isMediumDevice = useMediaQuery(`(min-width:${MD_WIDTH}px)`);
  const isLargeDevice = useMediaQuery(`(min-width:${LG_WIDTH}px)`);
  const isXLargeDevice = useMediaQuery(`(min-width:${XL_WIDTH}px)`);

  const showSideNav = process.env.REACT_APP_SHOW_SIDEBAR !== "false";

  const [openSideNav, setOpenSideNav] = useState(showSideNav && isLargeDevice);
  const [isTopNavHidden, setIsTopNavHidden] = useState(false);
  const toggleDrawer = useCallback((open: boolean) => {
    setOpenSideNav(open);
  }, []);
  const toggleHideTopNav = useCallback((isHidden: boolean) => {
    setIsTopNavHidden(isHidden);
  }, []);

  const NAV_CLOSED_DRAWER_WIDTH = showSideNav ? 64 : 0;
  const NAV_DRAWER_WIDTH = showSideNav ? 296 : 0;

  const isSidekickShowProductsChat = useMediaQuery(
    `(min-width:${
      openSideNav ? TABLE_PRODUCTS_MIN_WIDTH + NAV_DRAWER_WIDTH : TABLE_PRODUCTS_MIN_WIDTH + NAV_CLOSED_DRAWER_WIDTH
    }px)`,
  );
  const isSidekickShowCategoriesChat = useMediaQuery(
    `(min-width:${
      openSideNav
        ? TABLE_CATEGORIES_MIN_WIDTH + NAV_DRAWER_WIDTH
        : TABLE_CATEGORIES_MIN_WIDTH + NAV_CLOSED_DRAWER_WIDTH
    }px)`,
  );

  const isSmallHeightDevice = useMediaQuery("(max-height:750px)");

  const [animationsDisabled, setAnimationsDisabled] = useState(readAnimationsDisabled);
  const [colorMode, setColorModeState] = useState<PaletteMode>(readColorMode);
  const [dimensions, setDimensions] = useState<Dimensions>(() => ({
    height: typeof window !== "undefined" ? window.innerHeight : 0,
    width: typeof window !== "undefined" ? window.innerWidth : 0,
  }));

  const setColorModeFromBoolean = useCallback((isDarkTheme: boolean) => {
    const next: PaletteMode = isDarkTheme ? "dark" : "light";
    sessionStorage.setItem(COLOR_MODE_KEY, next);
    setColorModeState(next);
  }, []);

  const setColorMode = useCallback((m: PaletteMode) => {
    sessionStorage.setItem(COLOR_MODE_KEY, m);
    setColorModeState(m);
  }, []);

  const toggleColorMode = useCallback(() => {
    setColorModeState((prev) => {
      const next: PaletteMode = prev === "light" ? "dark" : "light";
      sessionStorage.setItem(COLOR_MODE_KEY, next);
      return next;
    });
  }, []);

  const setAnimationsDisabledFromBoolean = useCallback((v: boolean) => {
    sessionStorage.setItem(DISABLE_ANIMATION_KEY, JSON.stringify(v));
    setAnimationsDisabled(v);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        height: window.innerHeight,
        width: window.innerWidth,
      });
    };
    const debounced = debounce(handleResize, 500);
    window.addEventListener("resize", debounced);
    return () => {
      window.removeEventListener("resize", debounced);
      debounced.clear();
    };
  }, []);

  const value = useMemo<UIContextValue>(
    () => ({
      isMobile,
      isTablet,
      isSmallDevice,
      isSmallMediumDevice,
      isMediumDevice,
      isLargeDevice,
      isXLargeDevice,
      isSidekickShowProductsChat,
      isSidekickShowCategoriesChat,
      iOS,
      openSideNav,
      showSideNav,
      toggleDrawer,
      isTopNavHidden,
      toggleHideTopNav,
      NAV_CLOSED_DRAWER_WIDTH,
      NAV_DRAWER_WIDTH,
      colorMode,
      setColorMode,
      setColorModeFromBoolean,
      toggleColorMode,
      animationsDisabled,
      setAnimationsDisabledFromBoolean,
      dimensions,
      isSmallHeightDevice,
    }),
    [
      isMobile,
      isTablet,
      isSmallDevice,
      isSmallMediumDevice,
      isMediumDevice,
      isLargeDevice,
      isXLargeDevice,
      isSidekickShowProductsChat,
      isSidekickShowCategoriesChat,
      iOS,
      openSideNav,
      showSideNav,
      toggleDrawer,
      isTopNavHidden,
      toggleHideTopNav,
      NAV_CLOSED_DRAWER_WIDTH,
      NAV_DRAWER_WIDTH,
      colorMode,
      setColorMode,
      setColorModeFromBoolean,
      toggleColorMode,
      animationsDisabled,
      setAnimationsDisabledFromBoolean,
      dimensions,
      isSmallHeightDevice,
    ],
  );

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
}

export function useUIContext(): UIContextValue {
  const ctx = useContext(UIContext);
  if (!ctx) {
    throw new Error("useUIContext must be used within UIContextProvider");
  }
  return ctx;
}
